
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, CircleHelp } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";

interface GamePlanner2HeaderProps {
  onNewPlan: () => void;
}

export function GamePlanner2Header({
  onNewPlan
}: GamePlanner2HeaderProps) {
  const isMobile = useIsMobile();
  const [helpOpen, setHelpOpen] = useState(false);

  const helpContent = {
    title: "Game Planner Help",
    description: "Create and visualize your game plans",
    content: (
      <>
        <p>Welcome to the Game Planner! Here you can:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Create visual flowcharts of your techniques</li>
          <li>Plan competition strategies</li>
          <li>Create mental maps to help trigger reactions from any position</li>
          <li>Organize your game by positions and situations</li>
          <li>Save and share your game plans</li>
        </ul>
        <p>Use the controls to add positions, techniques, and decision points to build your plan.</p>
      </>
    )
  };
  
  if (isMobile) {
    // On mobile, MainLayout handles the header, so we only render the New Plan button
    return (
      <div className="flex justify-center my-4">
        <Button variant="default" onClick={onNewPlan} size="sm">
          <Plus className="mr-1.5 h-4 w-4" /> New Plan
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between mb-6">
      <h1 className="text-2xl font-bold inline-flex items-center gap-2">
        Game Planner
        <CircleHelp 
          className="h-5 w-5 text-primary cursor-pointer hover:text-primary/80 transition-colors" 
          onClick={() => setHelpOpen(true)}
          strokeWidth={2} 
        />
      </h1>
      <Button variant="default" onClick={onNewPlan} className="bg-black text-white hover:bg-black/90 px-3 py-1.5 h-auto ml-6" size="sm">
        <Plus className="mr-1.5 h-4 w-4" />
        New Plan
      </Button>

      {/* Help Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{helpContent.title}</DialogTitle>
            <DialogDescription>
              {helpContent.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {helpContent.content}
          </div>
          <div className="flex justify-end">
            <DialogClose asChild>
              <Button variant="default" size="sm">Got it</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
